<script lang="ts">
  // Self-contained pre-duel / chance overlays, driven entirely off the duel store:
  //   1. Turn-order choice, a SELECT_TP prompt surfaces a GO FIRST / GO SECOND modal (answered via
  //      chooseTp). NOTE: the current engine/core never emits MSG_SELECT_TP (first player is fixed to
  //      seat 0), so in live play this modal won't appear yet, it's wired and ready for a future host
  //      hook, and is exercised by the dev mock ("g" key).
  //   2. Coin/dice toss toast, a TOSS_COIN / TOSS_DICE info event (duel.coinDice) flashes the faces
  //      centre-screen, then auto-dismisses. These DO occur via card effects in live play.
  //
  // Matches the duel screen idiom: chamfer panels, font-display, cyan/gold/magenta, the `--c` chamfer
  // var. Mounted once near the modal/FX section of Duel.svelte.
  import { fade, scale } from "svelte/transition";
  import { duel, chooseTp } from "../../stores/duel.svelte.ts";
  import { draggableDialog } from "../../actions/draggableDialog.ts";

  // The turn-order prompt (go first / go second). Surfaces only for a selectTp prompt.
  const tp = $derived(duel.prompt && duel.prompt.kind === "selectTp" ? duel.prompt : null);
  // The transient coin/dice toss result.
  const cd = $derived(duel.coinDice);

  // Dice face → the pip layout (unicode dominoes read poorly small, so we draw pips).
  const PIPS: Record<number, [number, number][]> = {
    1: [[1, 1]],
    2: [[0, 0], [2, 2]],
    3: [[0, 0], [1, 1], [2, 2]],
    4: [[0, 0], [0, 2], [2, 0], [2, 2]],
    5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
    6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
  };
</script>

<!-- ══ turn-order choice (GO FIRST / GO SECOND) ══ -->
{#if tp}
  <div class="modal-scrim fixed inset-0 z-[55] grid place-items-center bg-void/48" in:fade={{ duration: 150 }}>
    <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Turn order" class="flex flex-col items-center gap-5 chamfer bg-surface/95 px-8 py-7 ring-1 ring-gold/45" style="--c:14px;box-shadow:0 0 40px color-mix(in oklab,var(--color-gold) 18%,transparent)" in:scale={{ duration: 200, start: 0.92 }}>
      <div data-dialog-drag-handle class="dialog-drag-handle -mx-3 -mt-4 flex w-full flex-col items-center gap-1 pt-2">
        <span class="font-display text-[0.62rem] uppercase tracking-[0.32em] text-gold/70">Turn Order</span>
        <span class="font-display text-lg font-bold uppercase tracking-wide text-ink">Will you go first?</span>
      </div>
      <div class="flex gap-4">
        <button
          class="chamfer-btn flex flex-col items-center gap-1 border border-gold/60 bg-void/70 px-7 py-3 font-display font-bold uppercase tracking-wider text-gold transition-colors hover:bg-gold/15"
          style="--c:7px"
          onclick={() => chooseTp(true)}
        >
          <span class="text-base">Go First</span>
          <span class="text-[0.55rem] font-semibold tracking-widest text-gold/60">draw initiative</span>
        </button>
        <button
          class="chamfer-btn flex flex-col items-center gap-1 border border-cyan/60 bg-void/70 px-7 py-3 font-display font-bold uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/15"
          style="--c:7px"
          onclick={() => chooseTp(false)}
        >
          <span class="text-base">Go Second</span>
          <span class="text-[0.55rem] font-semibold tracking-widest text-cyan/60">extra draw</span>
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- ══ coin / dice toss result toast ══ -->
{#if cd}
  {@const accent = cd.mine ? "var(--color-cyan)" : "var(--color-magenta)"}
  <div class="pointer-events-none fixed left-1/2 top-[30%] z-[52] flex -translate-x-1/2 flex-col items-center gap-3" in:scale={{ duration: 240, start: 0.8 }} out:fade={{ duration: 220 }}>
    <span class="chamfer bg-void/85 px-3 py-1 font-display text-[0.58rem] font-bold uppercase tracking-[0.22em] backdrop-blur" style="--c:4px;color:{accent};text-shadow:0 0 10px {accent}">
      {cd.mine ? "You" : duel.oppName} {cd.kind === "coin" ? "toss" : "roll"} {cd.kind === "coin" ? (cd.results.length > 1 ? "coins" : "a coin") : (cd.results.length > 1 ? "dice" : "a die")}
    </span>
    <div class="flex flex-wrap items-center justify-center gap-3">
      {#each cd.results as r, i (i)}
        {#if cd.kind === "coin"}
          <!-- coin: a chamfered disc that flips in; gold heads (1) / steel tails (0) -->
          <div
            class="cf-coin grid size-14 place-items-center chamfer font-display text-[0.62rem] font-bold uppercase tracking-wide"
            style="--c:50%;--accent:{accent};background:radial-gradient(circle at 38% 32%, color-mix(in oklab,{r ? 'var(--color-gold)' : '#9fb4cc'} 80%,#fff 12%), color-mix(in oklab,{r ? 'var(--color-gold)' : '#5d7088'} 70%, #000 18%));box-shadow:inset 0 0 0 2px color-mix(in oklab,{r ? 'var(--color-gold)' : '#9fb4cc'} 60%,transparent),0 0 18px color-mix(in oklab,{r ? 'var(--color-gold)' : '#9fb4cc'} 35%,transparent);animation-delay:{i * 120}ms"
          >
            <span class="text-void drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]">{r ? "Heads" : "Tails"}</span>
          </div>
        {:else}
          <!-- die: a chamfered cube face with drawn pips -->
          <div
            class="cf-die grid size-14 grid-cols-3 grid-rows-3 gap-0.5 chamfer bg-gradient-to-br from-ink to-[#cdd6e4] p-2"
            style="--c:8px;box-shadow:inset 0 0 0 1.5px color-mix(in oklab,{accent} 50%,transparent),0 0 18px color-mix(in oklab,{accent} 35%,transparent);animation-delay:{i * 120}ms"
          >
            {#each [0, 1, 2] as row (row)}
              {#each [0, 1, 2] as col (col)}
                <div class="grid place-items-center">
                  {#if PIPS[r]?.some(([pr, pc]) => pr === row && pc === col)}
                    <span class="size-2 rounded-full bg-void"></span>
                  {/if}
                </div>
              {/each}
            {/each}
          </div>
        {/if}
      {/each}
    </div>
  </div>
{/if}

<style>
  /* coin: a quick flip-in spin, then settle. */
  .cf-coin {
    animation: cf-flip 0.6s cubic-bezier(0.2, 0.8, 0.25, 1) both;
    transform-style: preserve-3d;
  }
  @keyframes cf-flip {
    0% { transform: rotateY(0) scale(0.5); opacity: 0; }
    60% { opacity: 1; }
    100% { transform: rotateY(720deg) scale(1); opacity: 1; }
  }
  /* die: a small tumble-in. */
  .cf-die {
    animation: cf-roll 0.55s cubic-bezier(0.2, 0.8, 0.25, 1) both;
  }
  @keyframes cf-roll {
    0% { transform: rotate(-180deg) scale(0.5); opacity: 0; }
    60% { opacity: 1; }
    100% { transform: rotate(0) scale(1); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .cf-coin, .cf-die { animation-duration: 0.01ms; }
  }
</style>
